package dev.krno.rive

import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event

/**
 * Boots the Kodee Rive animation on the `#kodee` canvas and returns a cleanup
 * lambda that unbinds all listeners. Idempotent and safe to call from a
 * `DisposableEffect`.
 *
 * Rive-side contract:
 * - Loads `assets/kodee.riv` and plays the `KodeeSM` state machine.
 * - The file is **data-bound**, not input-driven: `KodeeSM` exposes zero
 *   `stateMachineInputs`. Interaction goes through the default view model
 *   instance, which needs `autoBind = true` to be populated. Its properties are
 *   `hover` (boolean, held while the pointer is over the stage) and `celebrate`
 *   (trigger, fired on click).
 * - Keeps an `assetLoader` even though the current file embeds no images —
 *   omitting it leaves the load promise pending for files that do, and
 *   `onLoad` then never fires.
 * - On load: marks `#stage.loaded`, resizes the drawing surface, and binds the
 *   pointer/click handlers to the view model properties.
 */
internal fun bootKodeeRive(): () -> Unit {
    val canvas = document.getElementById("kodee") as? HTMLCanvasElement ?: return {}
    val stage = document.getElementById("stage") as? HTMLElement ?: return {}
    val console = window.asDynamic().console
    val riveG = window.asDynamic().rive
    if (riveG == null) {
        console.warn("Rive runtime not loaded")
        return {}
    }

    var instance: dynamic = null
    var observer: dynamic = null
    var hoverProp: dynamic = null
    var celebrateProp: dynamic = null

    val onEnter: (Event) -> Unit = {
        val hover = hoverProp
        if (hover != null) hover.value = true
    }
    val onLeave: (Event) -> Unit = {
        val hover = hoverProp
        if (hover != null) hover.value = false
    }
    val onClick: (Event) -> Unit = {
        celebrateProp?.trigger()
    }

    val layoutConfig: dynamic = js("({})")
    layoutConfig.fit = rive.Fit.Contain
    layoutConfig.alignment = rive.Alignment.Center
    val layout = __krno.newLayout(layoutConfig)

    val config: dynamic = js("({})")
    // Cache-buster: firebase.json serves *.riv as `immutable` for a year, so a
    // replaced animation at the same URL keeps rendering from disk cache. Bump
    // this whenever kodee.riv changes, and keep the preload in index.html in sync.
    config.src = "assets/kodee.riv?v=7"
    config.canvas = canvas
    config.autoplay = true
    config.stateMachines = arrayOf("KodeeSM")
    // Without autoBind the view model instance is null and the animation plays
    // but never reacts — the file carries no state-machine inputs to fall back on.
    config.autoBind = true
    config.layout = layout
    config.assetLoader = fun(asset: dynamic, bytes: dynamic): Boolean {
        if (asset.isImage == true && bytes != null && bytes.length > 0) {
            rive.decodeImage(bytes).then { img: dynamic ->
                asset.setRenderImage(img)
                img.unref()
            }
            return true
        }
        return false
    }
    config.onLoad = fun() {
        instance?.resizeDrawingSurfaceToCanvas()
        val vm = instance?.viewModelInstance
        if (vm != null) {
            hoverProp = try { vm.boolean("hover") } catch (_: Throwable) { null }
            celebrateProp = try { vm.trigger("celebrate") } catch (_: Throwable) { null }
            // Speed-line particles are drawn over the orange stage plate
            // (#FFA030 -> #F88909, see .hp-stage.stage). Rendered side by side,
            // both purples wash out against that orange — --k-purple #7F52FF is
            // also Kodee's own body colour, so particles crossing him vanish.
            // --k-black is the same ink as his visor and the chips and buttons
            // elsewhere on the plate, and it is the only option that stays
            // legible across the whole gradient.
            try { vm.color("particleColor").rgb(10, 10, 10) } catch (_: Throwable) {}
        } else {
            console.warn("Kodee view model missing — hover/click inert")
        }
        stage.addEventListener("pointerenter", onEnter)
        stage.addEventListener("pointerleave", onLeave)
        stage.addEventListener("click", onClick)
        observer = __krno.newResizeObserver {
            instance?.resizeDrawingSurfaceToCanvas()
        }
        observer.observe(stage)

        // Drop the poster only once Rive has actually painted. onLoad fires
        // before its first draw, so adding .loaded here directly flashes an
        // empty canvas between the poster going and Kodee arriving. One rAF
        // schedules the first draw, the second runs after it has landed.
        window.requestAnimationFrame {
            window.requestAnimationFrame { stage.classList.add("loaded") }
        }
    }
    config.onLoadError = fun(e: dynamic) {
        console.warn("Rive failed to load", e)
    }

    instance = __krno.newRive(config)

    return {
        stage.removeEventListener("pointerenter", onEnter)
        stage.removeEventListener("pointerleave", onLeave)
        stage.removeEventListener("click", onClick)
        if (observer != null) observer.disconnect()
        val inst = instance
        if (inst != null && inst.cleanup != null) inst.cleanup()
    }
}
